import React from 'react';
import type { InsightBoxProps } from '../InsightBox/InsightBox';
import { InsightBox } from '../InsightBox/InsightBox';
import { Box, Button, Icon, Paragraph, Text, TextButton } from '@zillow/constellation-10';
import { IconChevronDownFilled } from '@zillow/constellation-icons';
import { useIsScreenDesktop, useRmwPaths } from '@zg-rentals/rental-manager-env';
import { differenceInDays, format, isSameDay } from 'date-fns';

export interface PortfolioPerformanceInsightsProps {
  isLoading: boolean;
  filteredPropertiesReport: { portfolioInsights: Insights };
  isTotalMetrics?: boolean;
  areaId?: number;
  unitId?: number;
  startingDay: Date;
  endingDay: Date;
}

interface Insights {
  viewsAverage: number;
  impressionsAverage: number;
  leadsAverage: number;
  daysOnMarketAverage: number;
  reviewAverage: number;
  leadsRate: number;
  clickAverage: number;
}

export const PortfolioPerformanceInsights = ({
  isLoading,
  filteredPropertiesReport,
  isTotalMetrics,
  startingDay,
  endingDay,
}: PortfolioPerformanceInsightsProps) => {
  const paths = useRmwPaths();
  const [showMore, setShowMore] = React.useState(false);
  const [visibleInsightBoxes, setVisibleInsightBoxes] = React.useState<Array<InsightBoxProps>>([]);
  const isScreenDesktop = useIsScreenDesktop();
  const DEFAULT_METRICS_NUMBER_IN_MOBILE = 2;

  React.useEffect(() => {
    if (isScreenDesktop) {
      setVisibleInsightBoxes(totalInsightBoxes);
    } else {
      setVisibleInsightBoxes(totalInsightBoxes.slice(0, DEFAULT_METRICS_NUMBER_IN_MOBILE));
    }
  }, [filteredPropertiesReport, isScreenDesktop]);

  const totalInsightBoxes = React.useMemo(() => {
    if (!filteredPropertiesReport) {
      return [];
    }
    const { portfolioInsights } = filteredPropertiesReport;
    const prefix = isTotalMetrics ? 'Total ' : 'Avg. ';

    return [
      {
        value: portfolioInsights?.impressionsAverage || 0,
        metric: prefix + 'Impressions',
      },
      {
        value: portfolioInsights?.viewsAverage || 0,
        metric: prefix + 'Views',
      },
      {
        value: portfolioInsights?.leadsAverage || 0,
        metric: prefix + 'Leads',
      },
      {
        value: portfolioInsights?.clickAverage || 0,
        metric: (isTotalMetrics ? '' : 'Avg. ') + 'Listing Website Referrals',
      },
      {
        value: portfolioInsights?.daysOnMarketAverage || 0,
        metric: prefix + 'Days on Market',
      },
    ].filter((insightBox) => insightBox.value !== 0);
  }, [filteredPropertiesReport]);

  const totalMetricsNumber = React.useMemo(() => totalInsightBoxes.length, [filteredPropertiesReport]);

  if (isLoading || filteredPropertiesReport == null) return null;
  const marketInsightUrl = paths.analyticsPage + '/' + paths.analyticsMarketData;

  return (
    <>
      <Paragraph textStyle="body-bold" css={{ marginTop: '1200' }}>
        {getDisplayMessage(startingDay, endingDay, new Date())}
      </Paragraph>
      <Box css={{ display: 'flex', justifyContent: 'space-between' }}>
        <Text css={{ marginBottom: 'md', color: 'gray.600' }} textStyle="body-sm">
          Track and review statistics for your personal performance and your area.
        </Text>
        <TextButton asChild tone="brand" css={{ marginBottom: 'md' }}>
          <a href={marketInsightUrl}>Explore your Market</a>
        </TextButton>
      </Box>
      <Box css={{ display: 'flex', flexWrap: 'wrap' }}>
        {visibleInsightBoxes.map((insightBox) => (
          <InsightBox
            key={insightBox.metric}
            metric={insightBox.metric}
            value={insightBox.value}
            formatter={insightBox?.formatter}
          />
        ))}
      </Box>
      {(totalMetricsNumber > visibleInsightBoxes.length ||
        (!isScreenDesktop && totalMetricsNumber > DEFAULT_METRICS_NUMBER_IN_MOBILE)) && (
        <Button
          tone="neutral"
          emphasis="outlined"
          css={{
            marginBlock: 'xs',
            fontWeight: 'semibold',
            fontSize: 'sm',
          }}
          onClick={() => {
            const show = !showMore;
            setVisibleInsightBoxes(
              show ? totalInsightBoxes : totalInsightBoxes.slice(0, DEFAULT_METRICS_NUMBER_IN_MOBILE),
            );
            setShowMore(show);
          }}
        >
          {showMore ? 'Show less top performance metrics' : `Show all ${totalMetricsNumber} top performance metrics`}
          <Icon
            css={{
              marginLeft: 'xs',
              transform: showMore ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease',
            }}
          >
            <IconChevronDownFilled />
          </Icon>
        </Button>
      )}
    </>
  );
};

export const getDisplayMessage = (startingDay: Date, endingDay: Date, todayDate: Date) => {
  const endingDayToday = isSameDay(todayDate, endingDay);
  const endingDayYesterday = differenceInDays(todayDate, endingDay) == 1;
  const timeDelta = differenceInDays(endingDay, startingDay) + 1;

  if (endingDayToday && isSameDay(startingDay, endingDay)) {
    return 'Top performance metrics for today';
  }
  if (endingDayYesterday && isSameDay(startingDay, endingDay)) {
    return 'Top performance metrics for yesterday';
  }
  if (timeDelta == 1) {
    return `Top performance metrics for ${format(startingDay, 'MMMM, d yyyy')}`;
  }
  const trailingString = timeDelta > 2 || (timeDelta > 1 && !endingDayToday) ? 's' : '';
  if (endingDayToday) {
    return `Top performance metrics for the last ${timeDelta - 1} day${trailingString}, including today`;
  }
  if (endingDayYesterday) {
    return `Top performance metrics for the last ${timeDelta} day${trailingString}`;
  }
  return `Top performance metrics for ${format(startingDay, 'MMMM, d yyyy')} through ${format(
    endingDay,
    'MMMM, d yyyy',
  )}`;
};
